import { Injectable, DestroyRef, inject, signal, computed } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';

/**
 * Custom breakpoints for GOKURI application.
 * Using simple width-based queries (no orientation) for reliable detection in DevTools.
 * - Mobile/Handset: < 600px
 * - Tablet: 600px - 959px
 * - Desktop: >= 960px
 */
const CUSTOM_BREAKPOINTS = {
  Handset: '(max-width: 599.98px)',
  Tablet: '(min-width: 600px) and (max-width: 959.98px)',
  Desktop: '(min-width: 960px)',
};

/**
 * Service to track responsive layout breakpoints using signals.
 * Provides reactive state for device type detection (handset, tablet, desktop).
 */
@Injectable({ providedIn: 'root' })
export class LayoutBreakpointService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly breakpointObserver = inject(BreakpointObserver);

  // Initialize signals with current breakpoint state (not false)
  // This ensures computed signals get correct values immediately
  private readonly _isHandset = signal(this.checkHandset());
  private readonly _isTablet = signal(this.checkTablet());
  private readonly _isDesktop = signal(this.checkDesktop());

  /** True when viewport matches handset/mobile breakpoint (< 600px) */
  readonly isHandset = this._isHandset.asReadonly();

  /** True when viewport matches tablet breakpoint (600px - 959px) */
  readonly isTablet = this._isTablet.asReadonly();

  /** True when viewport is desktop size (>= 960px) */
  readonly isDesktop = this._isDesktop.asReadonly();

  /** Observable versions for components that use RxJS subscriptions */
  readonly isHandset$ = toObservable(this._isHandset);
  readonly isTablet$ = toObservable(this._isTablet);
  readonly isDesktop$ = toObservable(this._isDesktop);

  constructor() {
    // Observe all breakpoints using simple width-based queries
    this.breakpointObserver
      .observe([CUSTOM_BREAKPOINTS.Handset, CUSTOM_BREAKPOINTS.Tablet, CUSTOM_BREAKPOINTS.Desktop])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this._isHandset.set(state.breakpoints[CUSTOM_BREAKPOINTS.Handset] ?? false);
        this._isTablet.set(state.breakpoints[CUSTOM_BREAKPOINTS.Tablet] ?? false);
        this._isDesktop.set(state.breakpoints[CUSTOM_BREAKPOINTS.Desktop] ?? false);
      });
  }

  /** Synchronously check if current viewport is handset (< 600px) */
  private checkHandset(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(CUSTOM_BREAKPOINTS.Handset).matches;
  }

  /** Synchronously check if current viewport is tablet (600px - 959px) */
  private checkTablet(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(CUSTOM_BREAKPOINTS.Tablet).matches;
  }

  /** Synchronously check if current viewport is desktop (>= 960px) */
  private checkDesktop(): boolean {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(CUSTOM_BREAKPOINTS.Desktop).matches;
  }
}
